import { clsx } from 'clsx';
import React, {
  CSSProperties,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from 'react';

const VALID_KINDS = [
  'fade-out',
  'shrink-vertical',
  'shrink-horizontal',
  'rotate-left',
  'rotate-right',
];

export type AnimationKindType =
  | 'fade-out'
  | 'shrink-vertical'
  | 'shrink-horizontal'
  | 'rotate-left'
  | 'rotate-right';

export type AnimationType = {
  active: boolean;
  kind: AnimationKindType | AnimationKindType[];
  style?: CSSProperties;
  className?: string;
  delayInSeconds?: number;
  rotationDegree?: number;
  onAnimationEnd?: () => void;
  thresholds?: {
    shrinkHorizontal?: {
      begin?: number | string;
      end?: number | string;
    };
  };
};

export function Animation({
  active,
  children,
  kind,
  className,
  style,
  delayInSeconds = 0.2,
  rotationDegree = 180,
  thresholds,
  onAnimationEnd = () => {},
}: PropsWithChildren<AnimationType>) {
  const kinds = [kind].flat();
  const [isFirstRender, setIsFirstRender] = useState(true);

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
    } else {
      setTimeout(onAnimationEnd, delayInSeconds * 1000);
    }
    // No need of isFirstRender as a dependency, since it may cause an infinity loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delayInSeconds, active]);

  const styles = useMemo(() => {
    if (thresholds?.shrinkHorizontal) {
      const { begin, end } = thresholds.shrinkHorizontal;

      return {
        ...style,
        maxWidth: active ? end : begin,
      };
    }

    return style;
  }, [active, thresholds?.shrinkHorizontal, style]);

  const animationClasses = VALID_KINDS.reduce<Record<string, boolean>>(
    (acc, validKind) => {
      acc[`wk-animation__${validKind}`] = kinds.includes(
        validKind as AnimationKindType
      );
      acc[`wk-animation__${validKind}--active`] =
        kinds.includes(validKind as AnimationKindType) && active;

      return acc;
    },
    {}
  );

  return (
    <>
      <div
        className={clsx('wk-animation', className, animationClasses)}
        style={styles}
      >
        {children}
      </div>
      <style jsx>{`
        .wk-animation {
          overflow: hidden;
          transition:
            opacity ${delayInSeconds}s linear,
            max-height ${delayInSeconds}s linear,
            max-width ${delayInSeconds}s linear,
            transform ${delayInSeconds}s linear;
        }
        .wk-animation__fade-out {
          opacity: 1;
        }
        .wk-animation__fade-out--active {
          opacity: 0;
        }
        .wk-animation__shrink-vertical {
          max-height: 100%;
        }
        .wk-animation__shrink-vertical--active {
          max-height: 0;
        }
        .wk-animation__shrink-horizontal {
          max-width: 100%;
        }
        .wk-animation__shrink-horizontal--active {
          max-width: 0;
        }
        .wk-animation__rotate-left,
        .wk-animation__rotate-right {
          display: inline-block;
        }
        .wk-animation__rotate-left--active {
          transform: rotate(-${rotationDegree}deg);
        }
        .wk-animation__rotate-right--active {
          transform: rotate(${rotationDegree}deg);
        }
      `}</style>
    </>
  );
}
